import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FloppyDisk, ArrowLeft, Printer } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { spbPdfUrl } from "@/lib/api";
import SignatureGalleryPicker from "@/components/SignatureGalleryPicker";
import SoAllocationTable from "@/components/spb/SoAllocationTable";
import { devDebug } from "@/lib/debug";
import DataUtamaSection from "./spbCreate/DataUtamaSection";
import ProdukSection from "./spbCreate/ProdukSection";
import PelangganSection from "./spbCreate/PelangganSection";
import TrukSection from "./spbCreate/TrukSection";
import useSoAllocations, { MAX_SO_PER_SPB } from "./spbCreate/useSoAllocations";
import usePelangganSuggestions from "./spbCreate/usePelangganSuggestions";

export default function SPBCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [districts, setDistricts] = useState([]);
  const [products, setProducts] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [plants, setPlants] = useState([]);
  const activeTrucks = useMemo(() => trucks.filter((t) => t.aktif !== false), [trucks]);
  const [submitting, setSubmitting] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  // Alamat autocomplete: suggestions fetched as admin types nama_pelanggan.
  const [pelangganSuggestOpen, setPelangganSuggestOpen] = useState(false);

  const [form, setForm] = useState({
    tanggal: new Date().toISOString().slice(0, 10),
    distrik_id: "",
    plant_id: "",
    plant: "",
    tipe_pengiriman: "",
    no_so: "",
    produk_id: "",
    no_polisi: "",
    nama_supir: "",
    jumlah: "",
    satuan: "SAK",
    nama_barang: "",
    transporter: "",
    simpan_ke_master: false,
    truck_mode: "master",
    truck_id: "",
    slot_antrian: "",
    nama_pelanggan: "",
    alamat_pengiriman: "",
    keterangan: "",
    // Additional recipients (max 2 extras → total 3). Primary is stored in
    // nama_pelanggan/alamat_pengiriman above; extras appended here.
    extra_pelanggans: [], // [{ nama: "", alamat: "" }]
  });
  const [slotOptions, setSlotOptions] = useState([]);
  const [slotConflict, setSlotConflict] = useState(0);
  const [searchParams] = useSearchParams();
  const cloneId = searchParams.get("clone");
  const [cloneBanner, setCloneBanner] = useState(null); // { no_spb, cloned_from_id } when prefilled
  const [nextNoSpb, setNextNoSpb] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/districts", { params: { aktif: true } }),
      api.get("/products", { params: { aktif: true } }),
      api.get("/trucks"),
      api.get("/plants", { params: { aktif: true } }),
      api.get("/spb/slot-options"),
      api.get("/spb/next-number"),
    ]).then(([d, p, t, pl, so, nn]) => {
      setDistricts(d.data);
      setProducts(p.data);
      setTrucks(t.data);
      setPlants(pl.data);
      setSlotOptions(so.data.slots || []);
      setNextNoSpb(nn.data.next_no_spb);
    });
    // Bootstrap-once — deps are all module-scope (api) or setters (stable refs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If ?clone=<sid> in URL, fetch source SPB and pre-fill fields.
  // We keep customer/truck/product/distrik/etc; reset transient fields:
  // tanggal → today, no_so → empty, jumlah kept as reference, slot_antrian → empty.
  useEffect(() => {
    if (!cloneId) return;
    api.get(`/spb/${cloneId}`).then(({ data: src }) => {
      setForm((f) => ({
        ...f,
        tanggal: new Date().toISOString().slice(0, 10),
        distrik_id: src.distrik_id || "",
        plant_id: src.plant_id || "",
        plant: src.plant || "",
        tipe_pengiriman: src.tipe_pengiriman || "",
        produk_id: src.produk_id || "",
        no_polisi: src.no_polisi || "",
        nama_supir: src.nama_supir || "",
        jumlah: src.jumlah ? String(src.jumlah) : "",
        satuan: src.satuan || f.satuan,
        nama_barang: src.nama_barang || "",
        transporter: src.transporter || "",
        nama_pelanggan: src.nama_pelanggan || "",
        alamat_pengiriman: src.alamat_pengiriman || "",
        keterangan: src.keterangan || "",
        no_so: "",           // must pick a fresh SO
        slot_antrian: "",    // clone doesn't reuse queue slot
        truck_mode: "master",
      }));
      setCloneBanner({ no_spb: src.no_spb, id: cloneId });
      toast.success(`SPB ${src.no_spb} disalin — periksa & sesuaikan sebelum simpan`);
    }).catch((err) => {
      toast.error(`Gagal memuat SPB sumber: ${formatApiError(err)}`);
    });
    // Only refetch when cloneId (from URL) changes. Setters + toast are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloneId]);

  // Fetch slot count whenever slot/date changes to warn about double-booking
  useEffect(() => {
    if (!form.slot_antrian || !form.tanggal) {
      setSlotConflict(0);
      return;
    }
    api.get("/spb/slot-count", { params: { tanggal: form.tanggal, slot: form.slot_antrian } })
      .then(({ data }) => setSlotConflict(data.count || 0))
      .catch(() => setSlotConflict(0));
  }, [form.slot_antrian, form.tanggal]);

  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onSelectTruck = (id) => {
    const truck = trucks.find((t) => t.id === id);
    if (truck) {
      setF({
        truck_id: id,
        no_polisi: truck.no_polisi,
        nama_supir: truck.supir || "",
        transporter: truck.transporter || "",
      });
    }
  };

  const onSelectProduct = (id) => {
    const prod = products.find((p) => p.id === id);
    const patch = {
      produk_id: id,
      nama_barang: prod ? prod.nama : form.nama_barang,
    };
    // Auto-fill plant if product has a default mapped plant AND no plant selected yet.
    if (prod && prod.plant_id) {
      const pl = plants.find((x) => x.id === prod.plant_id);
      if (pl) {
        patch.plant_id = pl.id;
        patch.plant = pl.nama;
      }
    }
    setF(patch);
  };

  // Fetch available SOs whenever plant+product OR distrik change.
  // Distrik is used to route allocations: SOs bound to a specific district
  // only appear when that district is selected.
  // Admins additionally receive over-quota SOs (sisa ≤ 0) so they can
  // knowingly over-allocate when the physical truck differs from plan.
  const isAdmin = user?.role === "admin" || user?.role === "admin_kantor";
  const {
    availableSOs,
    soAllocations,
    manualAlloc,
    updateSoQty,
    removeSoRow,
    addSoRow,
    resetAutoFill,
    totalAllocated,
    jumlahNum,
    soShortfall,
    hasOverQuotaAlloc,
    availableSoTotals,
  } = useSoAllocations({
    plant_id: form.plant_id,
    produk_id: form.produk_id,
    distrik_id: form.distrik_id,
    jumlah: form.jumlah,
    isAdmin,
  });

  // Alamat autocomplete: debounced fetch of past pelanggan+alamat by name.
  const pelangganSuggestions = usePelangganSuggestions(form.nama_pelanggan);

  const applyPelangganSuggestion = (s) => {
    setF({
      nama_pelanggan: s.nama_pelanggan,
      alamat_pengiriman: s.alamat_pengiriman,
    });
    setPelangganSuggestOpen(false);
  };


  const submitSPB = async (andPrint = false) => {
    if (!form.distrik_id || !form.produk_id || !form.no_polisi || !form.jumlah) {
      toast.error("Lengkapi semua field wajib");
      return;
    }
    if (!form.nama_pelanggan.trim() || !form.alamat_pengiriman.trim()) {
      toast.error("Nama pelanggan & alamat pengiriman wajib diisi");
      return;
    }
    // Validate extra pelanggans — each must have both fields if present
    const cleanedExtras = (form.extra_pelanggans || [])
      .map((p) => ({ nama: (p.nama || "").trim(), alamat: (p.alamat || "").trim() }))
      .filter((p) => p.nama || p.alamat);
    for (const p of cleanedExtras) {
      if (!p.nama || !p.alamat) {
        toast.error("Tujuan tambahan wajib punya nama & alamat, atau hapus row-nya");
        return;
      }
    }
    if (cleanedExtras.length > 2) {
      toast.error("Maksimal 3 pelanggan per SPB");
      return;
    }
    // Guard: over-quota allocation requires explicit admin confirmation.
    if (hasOverQuotaAlloc) {
      const overList = soAllocations.filter((a) => a.over_quota).map((a) => a.no_so).join(", ");
      const ok = window.confirm(
        `SO ${overList} sudah melebihi kuota. Lanjutkan pembuatan SPB dengan over-quota?`
      );
      if (!ok) return;
    }
    // Guard: total alokasi harus == jumlah SPB (kalau ada SO available)
    if (soAllocations.length > 0 && Math.abs(totalAllocated - jumlahNum) > 0.01) {
      toast.error(
        `Total alokasi SO (${totalAllocated.toFixed(3)}) tidak sama dengan jumlah SPB (${jumlahNum.toFixed(3)}). Sesuaikan qty di tabel alokasi.`
      );
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        tanggal: form.tanggal,
        distrik_id: form.distrik_id,
        plant_id: form.plant_id || null,
        plant: form.plant,
        tipe_pengiriman: form.tipe_pengiriman,
        no_so: form.no_so,
        produk_id: form.produk_id,
        no_polisi: form.no_polisi,
        nama_supir: form.nama_supir,
        jumlah: parseFloat(form.jumlah),
        satuan: form.satuan,
        nama_barang: form.nama_barang,
        transporter: form.transporter,
        slot_antrian: form.slot_antrian || "",
        simpan_ke_master: form.truck_mode === "manual" && form.simpan_ke_master,
        signature_data: signatureData || "",
        so_allocations: soAllocations.length > 0 ? soAllocations : null,
        allow_over_quota: hasOverQuotaAlloc,
        nama_pelanggan: form.nama_pelanggan.trim(),
        alamat_pengiriman: form.alamat_pengiriman.trim(),
        keterangan: (form.keterangan || "").trim(),
        pelanggans: [
          { nama: form.nama_pelanggan.trim(), alamat: form.alamat_pengiriman.trim() },
          ...cleanedExtras,
        ],
      };
      const { data } = await api.post("/spb", payload);
      // Idempotent quick-add to master customers so typed names accumulate as
      // a suggestion source for future SPBs. Runs for EVERY recipient (primary
      // + extras). Silent failure — SPB create already succeeded.
      [form.nama_pelanggan.trim(), ...cleanedExtras.map((p) => p.nama)]
        .filter(Boolean)
        .forEach((nm) => {
          api.post("/customers/quick-add", { nama: nm }).catch(() => {});
        });
      toast.success(`SPB ${data.no_spb} berhasil dibuat`);
      if (andPrint) {
        // Open PDF in a hidden iframe and trigger print, then navigate
        const pdfHref = spbPdfUrl(data.id);
        const printWindow = window.open(pdfHref, "_blank");
        if (printWindow) {
          // Give the PDF viewer a moment to load then trigger print (best effort)
          setTimeout(() => {
            try { printWindow.print(); } catch (err) { devDebug("Auto-print failed; user can Ctrl+P manually", err); }
          }, 1500);
        } else {
          toast.info("Popup diblokir. Buka halaman detail untuk cetak manual.");
        }
      }
      navigate(`/spb/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    submitSPB(false);
  };

  const onSubmitAndPrint = () => {
    submitSPB(true);
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4"
        data-testid="back-button"
      >
        <ArrowLeft size={14} /> Kembali
      </button>

      <div className="border-b border-border pb-6 mb-8">
        <div className="overline">Buat Surat Baru</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">SPB Baru</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Nomor SPB akan digenerate otomatis oleh sistem.
        </p>
        {nextNoSpb && (
          <div
            className="mt-3 inline-flex items-center gap-2 border border-primary/40 bg-primary/5 px-3 py-2"
            data-testid="next-no-spb-preview"
          >
            <span className="overline text-primary">Nomor SPB Berikutnya</span>
            <span className="font-mono font-black text-lg tracking-wider" data-testid="next-no-spb-value">{nextNoSpb}</span>
          </div>
        )}
      </div>

      {cloneBanner && (
        <div
          className="mb-6 border-2 border-primary bg-primary/5 p-4 flex items-center justify-between gap-3"
          data-testid="clone-banner"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center font-black">
              ⧉
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-primary">Mode Duplikasi</div>
              <div className="text-sm mt-0.5">
                Form terisi dari <span className="font-mono font-bold">SPB {cloneBanner.no_spb}</span>.
                Tanggal, No SO, dan Slot Antrian sengaja dikosongkan — periksa sebelum simpan.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { setCloneBanner(null); navigate("/spb/new", { replace: true }); }}
            className="text-xs font-semibold underline text-muted-foreground hover:text-foreground"
            data-testid="clone-banner-clear"
          >
            Batal duplikasi
          </button>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-8" data-testid="spb-create-form">
        <DataUtamaSection
          form={form}
          setF={setF}
          districts={districts}
          plants={plants}
          slotOptions={slotOptions}
          slotConflict={slotConflict}
        />

        <ProdukSection
          form={form}
          setF={setF}
          products={products}
          onSelectProduct={onSelectProduct}
        />

        <PelangganSection
          form={form}
          setF={setF}
          suggestions={pelangganSuggestions}
          suggestOpen={pelangganSuggestOpen}
          setSuggestOpen={setPelangganSuggestOpen}
          applySuggestion={applyPelangganSuggestion}
        />

        <TrukSection
          form={form}
          setF={setF}
          activeTrucks={activeTrucks}
          trucks={trucks}
          onSelectTruck={onSelectTruck}
        />

        {/* Section 05 · Keterangan (opsional) */}
        <div className="border border-border p-6 bg-card">
          <div className="overline mb-4">05 · Keterangan (Opsional)</div>
          <Label className="overline block mb-2">Catatan / Keterangan</Label>
          <Textarea
            value={form.keterangan}
            onChange={(e) => setF({ keterangan: e.target.value })}
            className="rounded-lg min-h-[80px]"
            placeholder="Catatan tambahan untuk SPB ini (mis. instruksi khusus, catatan muatan)"
            data-testid="input-keterangan"
          />
        </div>

        {/* SO Allocation Panel */}
        {form.plant_id && form.produk_id && (
          <SoAllocationTable
            availableSOs={availableSOs}
            soAllocations={soAllocations}
            totalAllocated={totalAllocated}
            jumlahNum={jumlahNum}
            soShortfall={soShortfall}
            hasOverQuotaAlloc={hasOverQuotaAlloc}
            availableSoTotals={availableSoTotals}
            manualAlloc={manualAlloc}
            satuan={form.satuan}
            maxSoPerSpb={MAX_SO_PER_SPB}
            onResetAuto={resetAutoFill}
            onUpdateQty={updateSoQty}
            onAddSo={addSoRow}
            onRemoveSo={removeSoRow}
          />
        )}

        {/* Digital Signature panel — pick from library */}
        <div className="space-y-3" data-testid="signature-section">
          <div className="border-b border-border pb-3">
            <div className="overline">Tanda Tangan Digital</div>
            <p className="text-xs text-muted-foreground mt-1">
              Tanda tangan pertama di library Anda otomatis disematkan pada SPB. Klik gambar lain untuk mengganti, atau klik ulang untuk melepas.
            </p>
          </div>
          <SignatureGalleryPicker value={signatureData} onChange={setSignatureData} autoSelectFirst />
        </div>

        <div className="flex justify-end gap-3 flex-wrap">
          <Button type="button" variant="outline" className="rounded-lg" onClick={() => navigate(-1)}>
            Batal
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            variant="outline"
            className="rounded-lg h-11 font-bold uppercase tracking-wider min-w-40"
            data-testid="submit-spb-button"
          >
            <FloppyDisk size={16} className="mr-2" />
            {submitting ? "Menyimpan..." : "Simpan SPB"}
          </Button>
          <Button
            type="button"
            disabled={submitting}
            onClick={onSubmitAndPrint}
            className="rounded-lg h-11 font-bold uppercase tracking-wider min-w-40"
            data-testid="submit-and-print-button"
          >
            <Printer size={16} className="mr-2" />
            {submitting ? "Memproses..." : "Simpan & Cetak"}
          </Button>
        </div>
      </form>
    </div>
  );
}
